import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/configurators";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useConfigurators = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAllConfigurators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/");
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfiguratorById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/${id}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createConfigurator = useCallback(async (configuratorData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post("/", configuratorData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfigurator = useCallback(async (id, configuratorData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/${id}`, configuratorData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteConfigurator = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ===== Admin endpoints (manage configurators for specific companies) =====
  const getConfiguratorsByCompanyId = useCallback(async (companyId) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/company/${companyId}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfiguratorByIdForCompany = useCallback(async (companyId, id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/company/${companyId}/${id}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createConfiguratorForCompany = useCallback(
    async (companyId, configuratorData) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.post(
          `/company/${companyId}`,
          configuratorData,
        );
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateConfiguratorForCompany = useCallback(
    async (companyId, id, configuratorData) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.put(
          `/company/${companyId}/${id}`,
          configuratorData,
        );
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteConfiguratorForCompany = useCallback(async (companyId, id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/company/${companyId}/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ===== Configurator Requests =====
  const getConfiguratorRequests = useCallback(async (configuratorId) => {
    try {
      setLoading(true);
      setError(null);
      const params = configuratorId ? { configuratorId } : {};
      const { data } = await api.get("/requests", { params });
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfiguratorRequestById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/requests/${id}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfiguratorRequestStatus = useCallback(async (id, status) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/requests/${id}`, { status });
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteConfiguratorRequest = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/requests/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfiguratorRequestStats = useCallback(async () => {
    try {
      setError(null);
      const { data } = await api.get("/requests/stats");
      return data;
    } catch (error) {
      setError(error);
      throw error;
    }
  }, []);

  // ===== Admin endpoints for configurator requests =====
  const getConfiguratorRequestsByCompany = useCallback(
    async (companyId, configuratorId) => {
      try {
        setLoading(true);
        setError(null);
        const params = configuratorId ? { configuratorId } : {};
        const { data } = await api.get(`/company/${companyId}/requests`, {
          params,
        });
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getConfiguratorRequestStatsByCompany = useCallback(
    async (companyId) => {
      try {
        setError(null);
        const { data } = await api.get(`/company/${companyId}/requests/stats`);
        return data;
      } catch (error) {
        setError(error);
        throw error;
      }
    },
    [],
  );

  const updateConfiguratorRequestStatusByCompany = useCallback(
    async (companyId, id, status) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.put(`/company/${companyId}/requests/${id}`, {
          status,
        });
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteConfiguratorRequestByCompany = useCallback(
    async (companyId, id) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/company/${companyId}/requests/${id}`);
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    getAllConfigurators,
    getConfiguratorById,
    createConfigurator,
    updateConfigurator,
    deleteConfigurator,
    getConfiguratorsByCompanyId,
    getConfiguratorByIdForCompany,
    createConfiguratorForCompany,
    updateConfiguratorForCompany,
    deleteConfiguratorForCompany,
    getConfiguratorRequests,
    getConfiguratorRequestById,
    updateConfiguratorRequestStatus,
    deleteConfiguratorRequest,
    getConfiguratorRequestStats,
    getConfiguratorRequestsByCompany,
    getConfiguratorRequestStatsByCompany,
    updateConfiguratorRequestStatusByCompany,
    deleteConfiguratorRequestByCompany,
  };
};

export default useConfigurators;
